import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

/* Выбор времени в дизайне платформы — замена нативному <input type="time">.
   Значение снаружи в формате HH:MM, как у нативного инпута. Два столбца:
   часы и минуты с шагом step (по умолчанию 5 минут). */

const pad = (n) => String(n).padStart(2, '0');
const parse = (s) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(s || '');
  if (!m) return null;
  const h = +m[1], mi = +m[2];
  if (h > 23 || mi > 59) return null;
  return { h, m: mi };
};

const IcClock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" />
  </svg>
);

export default function TimePicker({ value, onChange, className = '', placeholder = 'Выберите время', disabled, step = 5 }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const cur = parse(value);
  const wrapRef = useRef(null);
  const popRef = useRef(null);
  const hRef = useRef(null);
  const mRef = useRef(null);

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const mins = Array.from({ length: Math.ceil(60 / step) }, (_, i) => i * step);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const r = wrapRef.current?.getBoundingClientRect();
      if (!r) return;
      const w = 196, h = 292;
      const left = Math.min(Math.max(8, r.left), window.innerWidth - w - 8);
      const below = r.bottom + 6;
      const top = below + h > window.innerHeight - 8 ? Math.max(8, r.top - h - 6) : below;
      setPos({ left, top, width: w });
    };
    // Прокрутку внутри столбцов игнорируем — иначе позиция пересчитывается
    // каждый кадр и списки «не листаются».
    const onScroll = (e) => { if (popRef.current?.contains(e.target)) return; place(); };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', onScroll, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', onScroll, true); };
  }, [open]);

  // Прокручиваем столбцы к выбранному значению, когда список открылся
  useEffect(() => {
    if (!open) return;
    [hRef, mRef].forEach(r => {
      const on = r.current?.querySelector('.pk-t.sel');
      if (on) r.current.scrollTop = on.offsetTop - r.current.clientHeight / 2 + on.clientHeight / 2;
    });
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const set = (h, m) => onChange(`${pad(h)}:${pad(m)}`);

  return (
    <div ref={wrapRef} className="pk-wrap">
      <button type="button" disabled={disabled} className={`pk-field ${className}${open ? ' open' : ''}${cur ? '' : ' empty'}`}
        onClick={() => !disabled && setOpen(o => !o)}>
        <span className="pk-ic"><IcClock /></span>
        <span className="pk-val">{cur ? `${pad(cur.h)}:${pad(cur.m)}` : placeholder}</span>
        {cur && !disabled && (
          <span className="pk-clear" role="button" title="Очистить"
            onClick={(e) => { e.stopPropagation(); onChange(''); setOpen(false); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </span>
        )}
      </button>

      {open && pos && createPortal(
        <div ref={popRef} className="pk-pop time" style={{ left: pos.left, top: pos.top, width: pos.width }}>
          <div className="pk-cols">
            <div className="pk-col">
              <div className="pk-col-cap">Часы</div>
              <div className="pk-col-list" ref={hRef}>
                {hours.map(h => (
                  <button type="button" key={h} className={`pk-t${cur && cur.h === h ? ' sel' : ''}`}
                    onClick={() => set(h, cur ? cur.m : 0)}>{pad(h)}</button>
                ))}
              </div>
            </div>
            <div className="pk-col">
              <div className="pk-col-cap">Минуты</div>
              <div className="pk-col-list" ref={mRef}>
                {mins.map(m => (
                  <button type="button" key={m} className={`pk-t${cur && cur.m === m ? ' sel' : ''}`}
                    onClick={() => { set(cur ? cur.h : 0, m); setOpen(false); }}>{pad(m)}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="pk-foot">
            <button type="button" className="pk-foot-btn" onClick={() => { const n = new Date(); set(n.getHours(), Math.round(n.getMinutes() / step) * step % 60); setOpen(false); }}>Сейчас</button>
            <button type="button" className="pk-foot-btn ghost" onClick={() => { onChange(''); setOpen(false); }}>Очистить</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
